// src/app/components/foundation/Button.tsx
import type { ButtonHTMLAttributes } from 'react';
import clsx from 'clsx';

export type ButtonVariant = 'primary' | 'secondary' | 'neutral' | 'danger' | 'ghost';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

// Solid variants (primary / secondary / danger): colored surface + an
// on-action text color, so contrast holds regardless of theme.
// Bordered/transparent variants (neutral / ghost): muted text with a
// subtle (or no) border, per the "no large CTA appearance" requirement.
const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    'border border-transparent bg-[var(--vania-accent,#5b8cff)] text-[var(--vania-text-on-action,#ffffff)] enabled:hover:bg-[var(--vania-action-primary-hover,#487af0)] enabled:active:bg-[var(--vania-action-primary-active,#3968de)]',
  secondary:
    'border border-transparent bg-[var(--vania-action-secondary,#2a2d36)] text-[var(--vania-text-primary,#f5f6f8)] enabled:hover:bg-[var(--vania-action-secondary-hover,#33363f)]',
  neutral:
    'border border-[var(--vania-colors-border-subtle,rgba(255,255,255,0.08))] bg-transparent text-[var(--vania-text-secondary,#a6acb8)] enabled:hover:bg-[var(--vania-colors-surface-hover,rgba(255,255,255,0.06))] enabled:hover:text-[var(--vania-text-primary,#f5f6f8)]',
  danger:
    'border border-transparent bg-[var(--vania-accent-rose,#ff6b81)] text-[var(--vania-text-on-action,#ffffff)] enabled:hover:brightness-110 enabled:active:brightness-95',
  ghost:
    'border border-transparent bg-transparent text-[var(--vania-text-secondary,#a6acb8)] enabled:hover:bg-[var(--vania-colors-surface-hover,rgba(255,255,255,0.06))] enabled:hover:text-[var(--vania-text-primary,#f5f6f8)]',
};

// Compact enterprise-admin sizing only -- height, padding, font-size,
// font-weight. No variant may change these; size and color are
// orthogonal so every variant stays visually consistent at a given size.
const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'h-7 px-2 text-xs font-medium',
  md: 'h-8 px-3 text-[13px] font-medium',
};

/**
 * Shared Foundation Button primitive for representative-admin. Token
 * driven (colors, radius, and focus ring all resolve through existing
 * `--vania-*` custom properties, no hardcoded hex values), compact
 * "enterprise admin" sizing (never a large marketing-CTA footprint), and
 * RTL compatible -- `dir` is inherited from an ancestor rather than set
 * here, and layout uses logical flex gap rather than physical left/right
 * spacing, so icon + label children reorder correctly under `dir="rtl"`.
 *
 * Purely presentational: no business logic, no API calls. All native
 * `<button>` props (`onClick`, `disabled`, `type`, ...) pass straight
 * through untouched.
 */
export function Button({
  variant = 'neutral',
  size = 'sm',
  disabled,
  className,
  type = 'button',
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled}
      aria-disabled={disabled}
      className={clsx(
        'inline-flex w-auto shrink-0 items-center justify-center gap-1.5 whitespace-nowrap',
        'rounded-[var(--vania-radius-sm,6px)] leading-none transition-colors',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--vania-border-focus,#5b8cff)]',
        'disabled:cursor-not-allowed disabled:opacity-60',
        SIZE_CLASSES[size],
        VARIANT_CLASSES[variant],
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}
